import {validateOwnerDayCloseReadModel} from '../../contracts/owner-dayclose-v1';
import type {OwnerSalesSlots,OwnerProductRanking} from '../../contracts/owner-sales-v1';
import {parseOwnerMemberAnalytics,parseOwnerMemberList} from './owner-member-read-model';
import {createOwnerCompletionClient} from './owner-dayclose-orders';
import type {OwnerDayCloseOrderInput} from './product-types';
import type {OwnerSettlementPage} from './product-types';
import type {OwnerDailyReports,OwnerAuthSession,OwnerChannelHealth,OwnerMemberAnalytics,OwnerMemberList,OwnerPlanningCommandResult,OwnerPlanningSaveInput,OwnerPlanningSnapshot,OwnerReadModelSnapshot,OwnerRuntimePort,OwnerSellabilityCommandInput,OwnerSellabilityCommandResult,OwnerSellabilityItem} from './product-types';

const OWNER_API_ORIGIN='https://admin.morefunos.com';
const STORE_ID='MF01';
const SESSION_KEY='mfk.owner.session.v1';

export class OwnerRuntimeError extends Error{
  readonly code:string;
  constructor(code:string,message?:string){super(message??code);this.code=code;}
}

function cleanSession(value:unknown):OwnerAuthSession|null{
  if(!value||typeof value!=='object'||Array.isArray(value))return null;
  const row=value as Record<string,unknown>;
  const staffId=String(row.staffId??'').trim();
  const loginId=String(row.loginId??row.staffId??'').trim();
  const displayName=String(row.displayName??'').trim();
  const sessionToken=String(row.sessionToken??'').trim();
  if(!staffId||!loginId||!displayName||String(row.role)!=='OWNER'||sessionToken.length<32)return null;
  return Object.freeze({
    staffId,loginId,displayName,role:'OWNER',
    scope:String(row.scope??'STORE'),
    permissions:Object.freeze(Array.isArray(row.permissions)?row.permissions.map(String):[]),
    sessionToken,
    ...(typeof row.expiresAt==='string'?{expiresAt:row.expiresAt}:{}),
  });
}
export function readStoredOwnerSession():OwnerAuthSession|null{
  if(typeof localStorage==='undefined')return null;
  try{const raw=localStorage.getItem(SESSION_KEY);return raw?cleanSession(JSON.parse(raw)):null;}catch{return null;}
}
function saveOwnerSession(value:OwnerAuthSession|null){
  if(typeof localStorage==='undefined')return;
  if(!value)localStorage.removeItem(SESSION_KEY);else localStorage.setItem(SESSION_KEY,JSON.stringify(value));
}
async function ownerFetch(path:string,init:RequestInit={}){
  let response:Response;
  try{response=await fetch(OWNER_API_ORIGIN+path,init);}catch{throw new OwnerRuntimeError('OWNER_NETWORK_ERROR','暫時未能連接 Owner 服務');}
  const body=await response.json().catch(()=>({})) as Record<string,unknown>;
  if(!response.ok)throw new OwnerRuntimeError(String(body.code||'OWNER_HTTP_'+response.status),String(body.message||body.code||'Owner 服務暫時不可用'));
  return body;
}
export async function refreshOwnerSession():Promise<OwnerAuthSession|null>{
  const current=readStoredOwnerSession();if(!current)return null;
  try{
    const body=await ownerFetch('/api/owner/auth/session?storeId='+encodeURIComponent(STORE_ID),{method:'GET',cache:'no-store',headers:{'x-mfk-owner-session':current.sessionToken}});
    const next=cleanSession({...body,sessionToken:current.sessionToken});if(!next){saveOwnerSession(null);return null;}saveOwnerSession(next);return next;
  }catch(error){
    if(error instanceof OwnerRuntimeError&&error.code==='OWNER_NETWORK_ERROR')return current;
    saveOwnerSession(null);return null;
  }
}
export async function loginOwner(loginId:string,pin:string):Promise<OwnerAuthSession>{
  const id=String(loginId||'').trim(),cleanPin=String(pin||'').replace(/\D/g,'');
  if(!id)throw new OwnerRuntimeError('OWNER_LOGIN_ID_REQUIRED','請輸入登入編號');
  if(cleanPin.length<4||cleanPin.length>8)throw new OwnerRuntimeError('OWNER_PIN_INVALID','PIN 必須為 4–8 位數字');
  const challenge=await ownerFetch('/api/owner/auth/challenge?storeId='+encodeURIComponent(STORE_ID),{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({loginId:id})});
  const challengeId=String(challenge.challengeId??'').trim(),nonce=String(challenge.nonce??'').trim();
  if(!challengeId||!nonce||challenge.authProtocol!=='MFK_SERVER_PIN_V2')throw new OwnerRuntimeError('OWNER_AUTH_CHALLENGE_INVALID');
  const body=await ownerFetch('/api/owner/auth/verify?storeId='+encodeURIComponent(STORE_ID),{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({loginId:id,challengeId,pin:cleanPin})});
  const session=cleanSession(body);if(!session)throw new OwnerRuntimeError('OWNER_SESSION_INVALID');saveOwnerSession(session);return session;
}
export async function logoutOwner(){
  const current=readStoredOwnerSession();saveOwnerSession(null);if(!current)return;
  try{await ownerFetch('/api/owner/auth/session?storeId='+encodeURIComponent(STORE_ID),{method:'POST',headers:{'x-mfk-owner-session':current.sessionToken}});}catch{}
}

function requireOwnerSession():OwnerAuthSession{
  const session=readStoredOwnerSession();
  if(!session)throw new OwnerRuntimeError('OWNER_SESSION_REQUIRED','請先登入 Owner App');
  return session;
}
function ownerSessionHeaders(extra:Record<string,string>={}){
  const session=requireOwnerSession();
  return {'x-mfk-owner-session':session.sessionToken,...extra};
}
export async function readOwnerChannels():Promise<readonly OwnerChannelHealth[]>{
  const body=await ownerFetch('/api/owner/channels?storeId='+encodeURIComponent(STORE_ID),{
    method:'GET',cache:'no-store',headers:ownerSessionHeaders(),
  });
  return Object.freeze(Array.isArray(body.channels)?body.channels as unknown as OwnerChannelHealth[]:[]);
}
export async function readOwnerMembers(query:string,offset=0):Promise<OwnerMemberList>{
  const body=await ownerFetch('/api/owner/members?storeId='+encodeURIComponent(STORE_ID)+'&q='+encodeURIComponent(query)+'&offset='+offset,{method:'GET',cache:'no-store',headers:ownerSessionHeaders()});
  const value=parseOwnerMemberList(body);if(!value)throw new OwnerRuntimeError('OWNER_MEMBER_RESPONSE_INVALID');return value;
}
export async function readOwnerMemberAnalytics():Promise<OwnerMemberAnalytics>{
  const body=await ownerFetch('/api/owner/member-analytics?storeId='+encodeURIComponent(STORE_ID),{method:'GET',cache:'no-store',headers:ownerSessionHeaders()});
  const value=parseOwnerMemberAnalytics(body);if(!value)throw new OwnerRuntimeError('OWNER_MEMBER_RESPONSE_INVALID');return value;
}
export async function readOwnerPlanning(monthKey:string):Promise<OwnerPlanningSnapshot>{
  const body=await ownerFetch('/api/owner/planning?storeId='+encodeURIComponent(STORE_ID)+'&monthKey='+encodeURIComponent(monthKey),{
    method:'GET',cache:'no-store',headers:ownerSessionHeaders(),
  });
  return body as unknown as OwnerPlanningSnapshot;
}
export async function saveOwnerPlanning(input:OwnerPlanningSaveInput):Promise<OwnerPlanningCommandResult>{
  const body=await ownerFetch('/api/owner/planning?storeId='+encodeURIComponent(STORE_ID),{
    method:'POST',
    headers:ownerSessionHeaders({'content-type':'application/json'}),
    body:JSON.stringify(input),
  });
  return body as unknown as OwnerPlanningCommandResult;
}
export async function readOwnerSellability():Promise<readonly OwnerSellabilityItem[]>{
  const body=await ownerFetch('/api/owner/sellability?storeId='+encodeURIComponent(STORE_ID),{
    method:'GET',cache:'no-store',headers:ownerSessionHeaders(),
  });
  return Object.freeze(Array.isArray(body.items)?body.items as unknown as OwnerSellabilityItem[]:[]);
}
export async function commandOwnerSellability(input:OwnerSellabilityCommandInput):Promise<OwnerSellabilityCommandResult>{
  const body=await ownerFetch('/api/owner/sellability?storeId='+encodeURIComponent(STORE_ID),{
    method:'POST',
    headers:ownerSessionHeaders({'content-type':'application/json'}),
    body:JSON.stringify(input),
  });
  return body as unknown as OwnerSellabilityCommandResult;
}
export async function readOwnerDayCloses(businessDate?:string){
  const query=new URLSearchParams({storeId:STORE_ID});if(businessDate)query.set('businessDate',businessDate);
  const body=await ownerFetch('/api/owner/day-closes?'+query,{method:'GET',cache:'no-store',headers:ownerSessionHeaders()});
  const decoded=validateOwnerDayCloseReadModel(body);
  if(decoded.businessDate!==(businessDate??null))throw Error('OWNER_DAYCLOSE_RESPONSE_DATE_MISMATCH');
  return decoded;
}
function ownerCompletionClient(){
  const session=requireOwnerSession();
  return createOwnerCompletionClient(session.staffId,localStorage,async command=>{
    const query=new URLSearchParams({storeId:STORE_ID});if(typeof command==='string')query.set('commandId',command);
    try{return await ownerFetch('/api/owner/order-commands?'+query,{method:typeof command==='string'?'GET':'POST',cache:'no-store',headers:ownerSessionHeaders({'content-type':'application/json'}),...(typeof command==='string'?{}:{body:JSON.stringify(command)})});}
    catch(error){if(typeof command==='string'&&error instanceof OwnerRuntimeError&&error.code==='WEB_COMMAND_NOT_FOUND')return {commandId:command,orderId:'',state:'NOT_FOUND'};throw error;}
  });
}
export const completeOwnerDayCloseOrder=(input:OwnerDayCloseOrderInput)=>ownerCompletionClient().complete(input);
export const readOwnerDayCloseOrderCommand=(orderId:string)=>ownerCompletionClient().read(orderId);
export async function readOwnerSalesSlots(businessDate:string,slot:15|30|60):Promise<OwnerSalesSlots>{
  const query=new URLSearchParams({storeId:STORE_ID,businessDate,slot:String(slot)});
  const value=await ownerFetch('/api/owner/sales-slots?'+query,{method:'GET',cache:'no-store',headers:ownerSessionHeaders()}) as unknown as OwnerSalesSlots;
  if(value.businessDate!==businessDate||value.slot!==slot||!Array.isArray(value.slots)||!Array.isArray(value.channels)||!Number.isSafeInteger(value.missingTimeOrders))throw new OwnerRuntimeError('OWNER_SALES_RESPONSE_INVALID');
  return value;
}
export async function readOwnerProductRanking(from:string,to:string,sort:'revenue'|'qty',low=false):Promise<OwnerProductRanking>{
  const query=new URLSearchParams({storeId:STORE_ID,from,to,sort,...(low?{low:'1',days:'30'}:{})});
  const value=await ownerFetch('/api/owner/product-ranking?'+query,{method:'GET',cache:'no-store',headers:ownerSessionHeaders()}) as unknown as OwnerProductRanking;
  if(value.to!==to||value.sort!==sort||value.low!==low||!Array.isArray(value.products)||!Array.isArray(value.neverSold)||!Number.isSafeInteger(value.revenueMinor))throw new OwnerRuntimeError('OWNER_RANKING_RESPONSE_INVALID');
  return value;
}
export async function readOwnerReports(from:string,to:string):Promise<OwnerDailyReports>{
  const query=new URLSearchParams({storeId:STORE_ID,from,to});
  const value=await ownerFetch('/api/owner/reports?'+query,{method:'GET',cache:'no-store',headers:ownerSessionHeaders()}) as unknown as OwnerDailyReports;
  if(value.from!==from||value.to!==to||!Array.isArray(value.days)||value.days.some(day=>day.date<from||day.date>to||!['grossMinor','refundMinor','netMinor','orders','cashSalesMinor','cashRefundMinor'].every(key=>Number.isSafeInteger(day[key as keyof typeof day]))))throw new OwnerRuntimeError('OWNER_REPORT_RESPONSE_INVALID');
  return value;
}
export async function readOwnerSettlements(after=''):Promise<OwnerSettlementPage>{
  const query=new URLSearchParams({storeId:STORE_ID,...(after?{after}:{})});
  const value=await ownerFetch('/api/owner/settlements?'+query,{method:'GET',cache:'no-store',headers:ownerSessionHeaders()}) as unknown as OwnerSettlementPage;
  if(!Array.isArray(value.records)||value.records.some(record=>!record||typeof record.recordId!=='string'||typeof record.channel!=='string'||['receivedMinor','knownReceivableMinor','differenceMinor'].some(key=>record[key as keyof typeof record]!==undefined&&!Number.isSafeInteger(record[key as keyof typeof record])))||!(value.cursor===null||typeof value.cursor==='string')||!Number.isFinite(Date.parse(value.observedAt)))throw new OwnerRuntimeError('OWNER_SETTLEMENT_RESPONSE_INVALID');
  return value;
}
export function createCloudOwnerRuntimePort():OwnerRuntimePort{
  return Object.freeze({
    portId:'MFK_OWNER_PORT_V1' as const,
    readOwnerSession:refreshOwnerSession,loginOwner,logoutOwner,
    readChannels:readOwnerChannels,readDayCloses:readOwnerDayCloses,
    completeDayCloseOrder:completeOwnerDayCloseOrder,readDayCloseOrderCommand:readOwnerDayCloseOrderCommand,
    readSalesSlots:readOwnerSalesSlots,readProductRanking:readOwnerProductRanking,readReports:readOwnerReports,
    readSettlements:readOwnerSettlements,
    readMembers:readOwnerMembers,readMemberAnalytics:readOwnerMemberAnalytics,
    readPlanning:readOwnerPlanning,
    savePlanning:saveOwnerPlanning,
    readSellability:readOwnerSellability,
    commandSellability:commandOwnerSellability,
    async readSnapshot():Promise<OwnerReadModelSnapshot>{
      const session=readStoredOwnerSession();if(!session)throw new OwnerRuntimeError('OWNER_SESSION_REQUIRED','請先登入 Owner App');
      const body=await ownerFetch('/api/owner/snapshot?storeId='+encodeURIComponent(STORE_ID),{method:'GET',cache:'no-store',headers:{'x-mfk-owner-session':session.sessionToken}});
      return body as unknown as OwnerReadModelSnapshot;
    },
  });
}
